import React, { useState, useEffect } from 'react';
import { fetchAgentData } from '../../hooks/useDeviceInfo';
import Icon from './Icon';

// Sanitiza texto con caracteres mal codificados del agente Windows
function sanitize(str) {
  if (!str) return str;
  return str
    .replace(/�/g, 'ó')  // replacement char común
    .replace(/ó/g, 'ó')
    .replace(/é/g, 'é')
    .replace(/á/g, 'á')
    .replace(/í/g, 'í')
    .replace(/ú/g, 'ú')
    .replace(/ñ/g, 'ñ');
}

const TABS = [
  { key:'info',      label:'Hardware',       icon:'computer'      },
  { key:'software',  label:'Software',       icon:'apps'          },
  { key:'processes', label:'Procesos',       icon:'settings'      },
  { key:'disks',     label:'Discos',         icon:'hard_drive'    },
  { key:'network',   label:'Red',            icon:'wifi'          },
  { key:'events',    label:'Eventos',        icon:'warning'       },
  { key:'updates',   label:'Updates',        icon:'system_update' },
  { key:'monitor',   label:'Monitorización', icon:'monitor_heart' },
];

/* ── Primitivas ───────────────────────────────────────── */

function InfoRow({ label, value, mono, accent }) {
  if (value === null || value === undefined || value === '' || value === 'undefinedh') return null;
  return (
    <div style={{
      display:'flex', justifyContent:'space-between', alignItems:'center',
      padding:'5px 0', borderBottom:'1px solid #F1F5F9', gap:24,
    }}>
      <span style={{fontSize:12,color:'#94A3B8',fontWeight:500,flexShrink:0,minWidth:110}}>{label}</span>
      <span style={{
        fontSize:12, fontWeight:600, color: accent ? 'var(--una-blue)' : '#1E293B',
        fontFamily: mono ? "'JetBrains Mono', 'Consolas', monospace" : 'inherit',
        textAlign:'right', wordBreak:'break-all',
      }}>{value}</span>
    </div>
  );
}

function SectionHead({ label }) {
  return (
    <div style={{
      fontSize:10, fontWeight:700, letterSpacing:1.2, textTransform:'uppercase',
      color:'#CBD5E1', marginBottom:4, marginTop:12, paddingBottom:4,
      borderBottom:'1.5px solid #F1F5F9',
    }}>{label}</div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div style={{padding:'32px 16px',textAlign:'center'}}>
      <div style={{fontSize:28,marginBottom:8,opacity:.4}}>{icon}</div>
      <div style={{fontSize:13,color:'#94A3B8'}}>{text}</div>
    </div>
  );
}

/* ── Contenido por pestaña ───────────────────────────── */

function Card({ title, icon, children }) {
  return (
    <div style={{background:'#F8FAFC',borderRadius:10,border:'1px solid #E2E8F0',padding:'12px 14px'}}>
      <div style={{fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',
        color:'#94A3B8',marginBottom:10,display:'flex',alignItems:'center',gap:5}}>
        {typeof icon === 'string' && icon.length > 2 ? <Icon name={icon} size={14} style={{marginRight:4}} /> : icon} {title}
      </div>
      {children}
    </div>
  );
}

function HardwareTab({ data }) {
  if (!data) return <EmptyState icon="🖥️" text="Sin datos de hardware"/>;
  const uptime = data.uptime_hours && !isNaN(data.uptime_hours) ? `${data.uptime_hours}h` : null;
  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
      <Card title="Sistema" icon="laptop_mac">
        <InfoRow label="Hostname"      value={data.hostname}   mono accent/>
        <InfoRow label="Usuario"       value={data.username?.split('\\').pop()}/>
        <InfoRow label="OS"            value={data.os}/>
        <InfoRow label="Build"         value={data.os_build}   mono/>
        <InfoRow label="Arquitectura"  value={data.os_arch}/>
        <InfoRow label="Último inicio" value={data.last_boot}/>
        {uptime && <InfoRow label="Tiempo activo" value={uptime}/>}
      </Card>
      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        <Card title="Hardware" icon="memory">
          <InfoRow label="Modelo"  value={data.model}/>
          <InfoRow label="Nº Serie" value={data.serial} mono/>
          <InfoRow label="CPU"     value={data.cpu}/>
          <InfoRow label="Núcleos" value={data.cpu_cores ? `${data.cpu_cores}c / ${data.cpu_threads}t` : null}/>
          <InfoRow label="RAM"     value={data.ram_gb ? `${data.ram_gb} GB` : null}/>
          <InfoRow label="GPU"     value={data.gpu}/>
        </Card>
        <Card title="Red" icon="wifi">
          <InfoRow label="IP Local" value={data.local_ip} mono accent/>
          <InfoRow label="MAC"      value={data.mac}      mono/>
        </Card>
      </div>
    </div>
  );
}

function SoftwareTab({ data, onAction }) {
  const [search,       setSearch]       = useState('');
  const [uninstalling, setUninstalling] = useState(null); // app name being uninstalled
  const [result,       setResult]       = useState(null); // { name, ok, msg }

  if (!data?.items?.length) return <EmptyState icon="📦" text="Sin programas instalados"/>;
  const filtered = data.items.filter(s => !search || s.name?.toLowerCase().includes(search.toLowerCase()));

  async function uninstall(appName) {
    if (!onAction) return;
    if (!confirm(`¿Desinstalar "${appName}"?\n\nEsta acción no se puede deshacer.`)) return;
    setUninstalling(appName);
    setResult(null);
    const script = `
$name = '${appName.replace(/'/g, "''")}'
$key = @(
  Get-ItemProperty "HKLM:\\Software\\Microsoft\\Windows\\CurrentVersion\\Uninstall\\*",
                   "HKLM:\\Software\\WOW6432Node\\Microsoft\\Windows\\CurrentVersion\\Uninstall\\*",
                   "HKCU:\\Software\\Microsoft\\Windows\\CurrentVersion\\Uninstall\\*" -ErrorAction SilentlyContinue |
  Where-Object { $_.DisplayName -eq $name }
) | Select-Object -First 1
if (-not $key) { "ERROR: Aplicación no encontrada en el registro"; exit }
$cmd = $key.UninstallString
if (-not $cmd) { "ERROR: No hay cadena de desinstalación"; exit }
# Añadir flags silenciosos comunes
$cmd = $cmd -replace '/I{', '/X{' # MSI repair→uninstall
$silent = if ($cmd -match 'msiexec') { "$cmd /quiet /norestart" } else { "$cmd /S /SILENT /quiet /norestart" }
try {
  $p = Start-Process cmd -ArgumentList "/c $silent" -Wait -PassThru -WindowStyle Hidden
  if ($p.ExitCode -eq 0) { "OK: $name desinstalado correctamente" }
  else { "WARN: Proceso terminó con código $($p.ExitCode) — verifica el equipo" }
} catch { "ERROR: $_" }`;
    try {
      const out = await onAction(script);
      const ok = out?.startsWith('OK');
      setResult({ name: appName, ok, msg: out || 'Sin respuesta' });
    } catch (e) {
      setResult({ name: appName, ok: false, msg: e.message });
    } finally {
      setUninstalling(null);
    }
  }

  return (
    <div>
      {result && (
        <div style={{
          marginBottom:10, padding:'9px 12px', borderRadius:8, fontSize:12,
          background: result.ok ? '#F0FDF4' : '#FEF2F2',
          border: `1px solid ${result.ok ? '#86EFAC' : '#FECACA'}`,
          color: result.ok ? '#15803D' : '#B91C1C',
          display:'flex', alignItems:'flex-start', gap:8,
        }}>
          <Icon name={result.ok ? 'check_circle' : 'error'} size={14} style={{color:'inherit',flexShrink:0,marginTop:1}}/>
          <div><b>{result.name}:</b> {result.msg}</div>
          <button onClick={() => setResult(null)} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',color:'inherit',fontSize:16,lineHeight:1}}>×</button>
        </div>
      )}
      <div style={{marginBottom:10}}>
        <input
          placeholder={`Buscar en ${data.items.length} programas...`}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width:'100%', height:34, padding:'0 12px',
            border:'1.5px solid #E2E8F0', borderRadius:8,
            fontSize:12, outline:'none', color:'#1E293B',
            fontFamily:'inherit', background:'#F8FAFC',
          }}
        />
      </div>
      <div style={{maxHeight:320,overflowY:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead style={{position:'sticky',top:0,background:'#F8FAFC',zIndex:1}}>
            <tr>
              {['Nombre','Versión','Editor', onAction ? '' : null].filter(Boolean).map(h=>(
                <th key={h} style={{padding:'7px 10px',textAlign:'left',fontSize:10,fontWeight:700,
                  color:'#94A3B8',letterSpacing:.6,textTransform:'uppercase',
                  borderBottom:'1.5px solid #E2E8F0'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((s,i)=>(
              <tr key={i} style={{borderBottom:'1px solid #F8FAFC'}}
                onMouseEnter={e => e.currentTarget.style.background='#F8FAFC'}
                onMouseLeave={e => e.currentTarget.style.background=''}
              >
                <td style={{padding:'6px 10px',fontWeight:600,color:'#1E293B'}}>{s.name}</td>
                <td style={{padding:'6px 10px',color:'#64748B',fontFamily:'monospace',fontSize:11}}>{s.version||'—'}</td>
                <td style={{padding:'6px 10px',color:'#94A3B8',maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.publisher||'—'}</td>
                {onAction && (
                  <td style={{padding:'4px 8px',textAlign:'right'}}>
                    <button
                      onClick={() => uninstall(s.name)}
                      disabled={!!uninstalling}
                      title="Desinstalar"
                      style={{
                        padding:'3px 8px', borderRadius:5, border:'1px solid #FECACA',
                        background: uninstalling===s.name ? '#F3F4F6' : '#FFF5F5',
                        color: uninstalling===s.name ? '#9CA3AF' : '#DC2626',
                        fontSize:10, fontWeight:600, cursor: uninstalling ? 'not-allowed' : 'pointer',
                        display:'inline-flex', alignItems:'center', gap:3, whiteSpace:'nowrap',
                      }}>
                      {uninstalling===s.name
                        ? <><Icon name="hourglass_empty" size={11} style={{color:'inherit'}}/> Desinstalando...</>
                        : <><Icon name="delete" size={11} style={{color:'inherit'}}/> Desinstalar</>
                      }
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProcessesTab({ data, onAction }) {
  const [killing, setKilling] = useState(null); // pid being killed
  const [result,  setResult]  = useState(null);

  if (!data?.items?.length) return <EmptyState icon="⚙️" text="Sin procesos"/>;
  const maxRAM = Math.max(...(data.items||[]).map(p => p.ram_mb||0));

  // System processes that should not be killed
  const PROTECTED = ['system','svchost','lsass','csrss','wininit','winlogon','smss','services','registry'];

  async function killProcess(proc) {
    if (!onAction) return;
    if (PROTECTED.includes(proc.name.toLowerCase())) {
      alert(`"${proc.name}" es un proceso del sistema y no puede terminarse.`);
      return;
    }
    if (!confirm(`¿Terminar proceso "${proc.name}" (PID ${proc.pid})?\n\nEl proceso se cerrará inmediatamente sin guardar datos.`)) return;
    setKilling(proc.pid);
    setResult(null);
    const script = `
try {
  $p = Get-Process -Id ${proc.pid} -ErrorAction Stop
  $name = $p.ProcessName
  Stop-Process -Id ${proc.pid} -Force -ErrorAction Stop
  "OK: Proceso '$name' (PID ${proc.pid}) terminado correctamente"
} catch {
  "ERROR: $_"
}`;
    try {
      const out = await onAction(script);
      setResult({ pid: proc.pid, name: proc.name, ok: out?.startsWith('OK'), msg: out });
    } catch (e) {
      setResult({ pid: proc.pid, name: proc.name, ok: false, msg: e.message });
    } finally {
      setKilling(null);
    }
  }

  return (
    <div>
      {result && (
        <div style={{
          marginBottom:10, padding:'9px 12px', borderRadius:8, fontSize:12,
          background: result.ok ? '#F0FDF4' : '#FEF2F2',
          border: `1px solid ${result.ok ? '#86EFAC' : '#FECACA'}`,
          color: result.ok ? '#15803D' : '#B91C1C',
          display:'flex', alignItems:'flex-start', gap:8,
        }}>
          <Icon name={result.ok ? 'check_circle' : 'error'} size={14} style={{color:'inherit',flexShrink:0,marginTop:1}}/>
          <div><b>{result.name} ({result.pid}):</b> {result.msg}</div>
          <button onClick={() => setResult(null)} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',color:'inherit',fontSize:16,lineHeight:1}}>×</button>
        </div>
      )}
      <div style={{fontSize:11,color:'#94A3B8',marginBottom:10}}>
        Top 40 por uso de RAM · {data.captured_at}
      </div>
      <div style={{maxHeight:340,overflowY:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead style={{position:'sticky',top:0,background:'#F8FAFC'}}>
            <tr>
              {['Proceso','PID','RAM','Hilos', onAction ? '' : null].filter(Boolean).map(h=>(
                <th key={h} style={{padding:'7px 10px',textAlign:'left',fontSize:10,fontWeight:700,
                  color:'#94A3B8',letterSpacing:.6,textTransform:'uppercase',
                  borderBottom:'1.5px solid #E2E8F0'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.items||[]).map((p,i) => {
              const isProtected = PROTECTED.includes(p.name.toLowerCase());
              return (
                <tr key={i} style={{borderBottom:'1px solid #F8FAFC'}}>
                  <td style={{padding:'6px 10px',fontWeight:600,color: isProtected ? '#94A3B8' : '#1E293B'}}>{p.name}</td>
                  <td style={{padding:'6px 10px',color:'#94A3B8',fontFamily:'monospace',fontSize:11}}>{p.pid}</td>
                  <td style={{padding:'6px 10px',minWidth:120}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <div style={{flex:1,height:4,borderRadius:2,background:'#E2E8F0',overflow:'hidden'}}>
                        <div style={{
                          width:`${Math.min(100, (p.ram_mb/maxRAM)*100)}%`,
                          height:'100%', borderRadius:2,
                          background: p.ram_mb > 500 ? '#EF4444' : p.ram_mb > 200 ? '#F59E0B' : 'var(--una-blue)',
                          transition:'width .3s',
                        }}/>
                      </div>
                      <span style={{fontSize:11,color:'#475569',width:52,textAlign:'right',flexShrink:0}}>{p.ram_mb}MB</span>
                    </div>
                  </td>
                  <td style={{padding:'6px 10px',color:'#94A3B8',fontSize:11}}>{p.threads}</td>
                  {onAction && (
                    <td style={{padding:'4px 8px',textAlign:'right'}}>
                      {!isProtected && (
                        <button
                          onClick={() => killProcess(p)}
                          disabled={!!killing}
                          title="Terminar proceso"
                          style={{
                            padding:'3px 8px', borderRadius:5, border:'1px solid #FECACA',
                            background: killing===p.pid ? '#F3F4F6' : '#FFF5F5',
                            color: killing===p.pid ? '#9CA3AF' : '#DC2626',
                            fontSize:10, fontWeight:600, cursor: killing ? 'not-allowed' : 'pointer',
                            display:'inline-flex', alignItems:'center', gap:3,
                          }}>
                          {killing===p.pid
                            ? <><Icon name="hourglass_empty" size={11} style={{color:'inherit'}}/> Terminando...</>
                            : <><Icon name="close" size={11} style={{color:'inherit'}}/> Terminar</>
                          }
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DisksTab({ data }) {
  if (!data?.items?.length) return <EmptyState icon="💾" text="Sin discos detectados"/>;
  return (
    <div style={{display:'flex',flexDirection:'column',gap:10}}>
      {(data.items||[]).map((d,i) => {
        const pct = d.used_pct || 0;
        const color = pct > 90 ? '#EF4444' : pct > 75 ? '#F59E0B' : 'var(--una-blue)';
        return (
          <div key={i} style={{
            padding:'14px 16px', background:'#F8FAFC',
            borderRadius:10, border:`1px solid ${pct>90?'#FEE2E2':pct>75?'#FEF3C7':'#E2E8F0'}`,
          }}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:18}}>💾</span>
                <div>
                  <span style={{fontSize:14,fontWeight:700,color:'#1E293B'}}>{d.drive}</span>
                  {d.label && <span style={{fontSize:12,color:'#94A3B8',marginLeft:6}}>{d.label}</span>}
                </div>
              </div>
              <span style={{fontSize:13,fontWeight:700,color}}>{pct}% usado</span>
            </div>
            <div style={{height:6,borderRadius:3,background:'#E2E8F0',marginBottom:10}}>
              <div style={{width:`${pct}%`,height:'100%',borderRadius:3,background:color,transition:'width .5s ease'}}/>
            </div>
            <div style={{display:'flex',gap:16,fontSize:12}}>
              <span style={{color:'#94A3B8'}}>Total <b style={{color:'#475569'}}>{d.total_gb} GB</b></span>
              <span style={{color:'#94A3B8'}}>Usado <b style={{color:'#475569'}}>{d.used_gb} GB</b></span>
              <span style={{color:'#94A3B8'}}>Libre <b style={{color:'#15803D'}}>{d.free_gb} GB</b></span>
              <span style={{marginLeft:'auto',color:'#CBD5E1',fontSize:11}}>{d.filesystem}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NetworkTab({ data }) {
  if (!data?.adapters?.length) return <EmptyState icon="wifi" text="Sin adaptadores de red"/>;
  return (
    <div>
      {data.dns_servers?.length > 0 && (
        <div style={{marginBottom:12,padding:'8px 12px',background:'#EFF6FF',borderRadius:8,
          fontSize:12,display:'flex',alignItems:'center',gap:8}}>
          <span style={{color:'#94A3B8',fontWeight:500}}>DNS:</span>
          <span style={{fontFamily:'monospace',fontWeight:600,color:'var(--una-blue)'}}>{data.dns_servers.join(' · ')}</span>
        </div>
      )}
      {(data.adapters||[]).map((a,i) => (
        <div key={i} style={{
          marginBottom:10,padding:'12px 14px',
          background:'#F8FAFC',borderRadius:10,
          border:`1px solid ${a.status==='Up'?'#BBF7D0':'#E2E8F0'}`,
        }}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <span style={{fontSize:13,fontWeight:700,color:'#1E293B'}}>{sanitize(a.name)}</span>
            <span style={{
              fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:10,
              background: a.status==='Up'?'#F0FDF4':'#F1F5F9',
              color: a.status==='Up'?'#15803D':'#94A3B8',
              border: `1px solid ${a.status==='Up'?'#86EFAC':'#E2E8F0'}`,
            }}>● {a.status}</span>
          </div>
          <div style={{fontSize:11,color:'#94A3B8',marginBottom:8}}>{sanitize(a.description)}</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4}}>
            <InfoRow label="IP"        value={a.ip}         mono accent/>
            <InfoRow label="MAC"       value={a.mac}        mono/>
            <InfoRow label="Velocidad" value={a.speed_mbps ? `${a.speed_mbps} Mbps` : null}/>
            <InfoRow label="Enviado"   value={a.sent_mb ? `${a.sent_mb} MB` : null}/>
          </div>
        </div>
      ))}
    </div>
  );
}

function EventsTab({ data }) {
  if (!data?.items?.length) return (
    <div style={{padding:'32px 16px',textAlign:'center'}}>
      <div style={{fontSize:28,marginBottom:8}}>✅</div>
      <div style={{fontSize:13,color:'#15803D',fontWeight:600}}>Sin errores recientes</div>
      <div style={{fontSize:12,color:'#94A3B8',marginTop:4}}>Últimos 3 días sin eventos críticos</div>
    </div>
  );
  return (
    <div style={{display:'flex',flexDirection:'column',gap:8,maxHeight:340,overflowY:'auto'}}>
      {(data.items||[]).map((e,i) => (
        <div key={i} style={{
          padding:'10px 12px',borderRadius:8,
          background: e.level==='Error'?'#FFF5F5':'#FFFBEB',
          border: `1px solid ${e.level==='Error'?'#FECACA':'#FDE68A'}`,
        }}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:4}}>
            <div style={{display:'flex',alignItems:'center',gap:6}}>
              <span style={{fontSize:12,fontWeight:700,color:e.level==='Error'?'#EF4444':'#F59E0B'}}>
                {e.level==='Error'?'⚠':'⚡'} {e.source}
              </span>
              <span style={{fontSize:10,color:'#CBD5E1',fontFamily:'monospace'}}>ID:{e.id}</span>
            </div>
            <span style={{fontSize:10,color:'#94A3B8',whiteSpace:'nowrap',flexShrink:0,marginLeft:8}}>{e.time}</span>
          </div>
          <div style={{fontSize:12,color:'#475569',lineHeight:1.5}}>{e.message}</div>
        </div>
      ))}
    </div>
  );
}

function UpdatesTab({ data }) {
  // Normalizar todos los campos por si vienen en formato inesperado
  const pending      = Array.isArray(data?.pending)        ? data.pending        : [];
  const lastInstalled = Array.isArray(data?.last_installed) ? data.last_installed : [];
  const pendingCount = typeof data?.pending_count === 'number' ? data.pending_count : pending.length;

  return (
    <div>
      {pendingCount > 0 ? (
        <div style={{
          marginBottom:16,padding:'12px 14px',
          background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:10,
        }}>
          <div style={{fontSize:13,fontWeight:700,color:'#B45309',marginBottom:10}}>
            ⚠ {pendingCount} actualizaciones pendientes
          </div>
          {pending.map((u,i)=>(
            <div key={i} style={{
              fontSize:12,color:'#475569',padding:'5px 0',
              borderBottom:'1px solid #FEF3C7',display:'flex',justifyContent:'space-between',gap:8,
            }}>
              <span style={{flex:1}}>{String(u?.title||'—')}</span>
              {u?.kb && <span style={{color:'#94A3B8',fontFamily:'monospace',fontSize:11,flexShrink:0}}>KB{u.kb}</span>}
            </div>
          ))}
        </div>
      ) : (
        <div style={{marginBottom:16,padding:'10px 14px',background:'#F0FDF4',border:'1px solid #86EFAC',borderRadius:10,fontSize:12,color:'#15803D',fontWeight:600}}>
          ✓ Sistema actualizado
        </div>
      )}
      <div style={{fontSize:11,fontWeight:700,letterSpacing:.8,textTransform:'uppercase',color:'#CBD5E1',marginBottom:8}}>Últimas instaladas</div>
      {lastInstalled.length > 0 ? lastInstalled.map((u,i)=>(
        <div key={i} style={{
          display:'flex',justifyContent:'space-between',alignItems:'center',
          padding:'7px 0',borderBottom:'1px solid #F1F5F9',gap:8,fontSize:12,
        }}>
          <span style={{color:'#475569',flex:1,lineHeight:1.4}}>{String(u?.title||'—')}</span>
          <div style={{flexShrink:0,textAlign:'right'}}>
            <div style={{fontSize:11,color:'#94A3B8'}}>{u?.date||''}</div>
            <div style={{fontSize:11,fontWeight:600,color:['OK','Éxito','xito'].some(s=>String(u?.result||'').includes(s))?'#15803D':'#EF4444'}}>{u?.result||'—'}</div>
          </div>
        </div>
      )) : (
        <div style={{fontSize:12,color:'#94A3B8',textAlign:'center',padding:16}}>Sin historial disponible</div>
      )}
    </div>
  );
}


// Transform stored inventory (from server DB) into the format each tab expects
function inventoryToTabData(tabKey, inventory) {
  if (!inventory) return null;
  switch (tabKey) {
    case 'info':      return inventory.hardware;
    case 'software':  return { ok:true, items: inventory.software  || [], count: (inventory.software||[]).length };
    case 'processes': return { ok:true, items: inventory.processes || [], captured_at: inventory.collected_at };
    case 'disks':     return { ok:true, items: inventory.disks     || [] };
    case 'network':   return { ok:true, adapters: inventory.network || [], dns_servers: [] };
    case 'events':    return { ok:true, items: [] };
    case 'updates':   return { ok:true, pending_count:0, pending:[], last_installed:[] };
    default:          return null;
  }
}

/* ── Monitor Tab (live data passed from parent) ──────── */

function MetricBar({ label, pct, valueLabel, color }) {
  const c = color || (pct > 85 ? '#DC2626' : pct > 65 ? '#D97706' : '#22C55E');
  return (
    <div style={{marginBottom:16}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
        <span style={{fontSize:12,fontWeight:600,color:'#374151'}}>{label}</span>
        <span style={{fontSize:12,fontWeight:700,color:c}}>{valueLabel}</span>
      </div>
      <div style={{height:10,background:'#F1F5F9',borderRadius:6,overflow:'hidden'}}>
        <div style={{height:'100%',width:`${Math.min(100,pct||0)}%`,background:c,borderRadius:6,transition:'width .6s ease'}}/>
      </div>
    </div>
  );
}

function MonitorTab({ monitoring }) {
  if (!monitoring || monitoring.cpu_pct === undefined) {
    return (
      <div style={{padding:'48px 24px',textAlign:'center'}}>
        <div style={{
          width:48,height:48,borderRadius:'50%',
          background:'#F1F5F9',display:'flex',alignItems:'center',justifyContent:'center',
          margin:'0 auto 14px',
        }}>
          <Icon name="monitor_heart" size={24} style={{color:'#CBD5E1'}}/>
        </div>
        <div style={{fontSize:13,fontWeight:600,color:'#94A3B8',marginBottom:6}}>Sin datos de monitorización</div>
        <div style={{fontSize:12,color:'#CBD5E1'}}>
          El agente enviará métricas en tiempo real cuando esté conectado.
        </div>
      </div>
    );
  }

  const ts = monitoring.timestamp
    ? new Date(monitoring.timestamp).toLocaleTimeString('es-ES')
    : null;

  return (
    <div>
      {/* Live badge */}
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:20}}>
        <span style={{
          display:'inline-flex',alignItems:'center',gap:5,
          padding:'3px 10px',borderRadius:12,
          background:'#F0FDF4',border:'1px solid #86EFAC',
          fontSize:11,fontWeight:700,color:'#15803D',
        }}>
          <span style={{
            width:6,height:6,borderRadius:'50%',background:'#22C55E',
            display:'inline-block',animation:'pulse-live 1.5s infinite',
          }}/>
          EN VIVO
        </span>
        {ts && <span style={{fontSize:11,color:'#9CA3AF'}}>Actualizado {ts}</span>}
        <style>{`@keyframes pulse-live{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      </div>

      {/* CPU */}
      <MetricBar
        label="CPU"
        pct={monitoring.cpu_pct}
        valueLabel={`${monitoring.cpu_pct}%`}
      />

      {/* RAM */}
      <MetricBar
        label="Memoria RAM"
        pct={monitoring.ram_pct}
        valueLabel={`${monitoring.ram_used_gb} GB / ${monitoring.ram_total_gb} GB (${monitoring.ram_pct}%)`}
        color={monitoring.ram_pct > 85 ? '#DC2626' : monitoring.ram_pct > 70 ? '#D97706' : '#3B82F6'}
      />

      {/* Disks */}
      {monitoring.disks?.length > 0 && (
        <>
          <div style={{fontSize:11,fontWeight:700,color:'#CBD5E1',textTransform:'uppercase',letterSpacing:1,marginBottom:12,marginTop:4}}>
            Discos
          </div>
          {monitoring.disks.map(disk => (
            <MetricBar
              key={disk.drive}
              label={`Disco ${disk.drive}`}
              pct={disk.used_pct}
              valueLabel={`${disk.free_gb} GB libres de ${disk.total_gb} GB (${disk.used_pct}%)`}
              color={disk.used_pct > 90 ? '#DC2626' : disk.used_pct > 75 ? '#D97706' : '#8B5CF6'}
            />
          ))}
        </>
      )}
    </div>
  );
}

function TabContent({ tabKey, deviceInfo, inventory, monitoring, onAction }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (tabKey === 'monitor') { setLoading(false); return; } // handled by MonitorTab
    // If full inventory is provided (ComputerDetail), use it — don't call local agent
    if (inventory) {
      setData(inventoryToTabData(tabKey, inventory));
      setLoading(false);
      return;
    }
    if (tabKey === 'info') { setData(deviceInfo); setLoading(false); return; }
    setLoading(true); setError('');
    fetchAgentData(tabKey).then(d => {
      setData(d);
      if (!d?.ok) setError(d?.error || 'Error');
    }).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [tabKey, inventory]);

  if (loading) return (
    <div style={{padding:'32px 16px',textAlign:'center'}}>
      <div style={{width:24,height:24,border:'2px solid #E2E8F0',borderTopColor:'var(--una-blue)',
        borderRadius:'50%',animation:'spin 1s linear infinite',margin:'0 auto 8px'}}/>
      <div style={{fontSize:12,color:'#94A3B8'}}>Cargando...</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error) return (
    <div style={{padding:'24px 16px',textAlign:'center'}}>
      <div style={{fontSize:24,marginBottom:8}}>⚠️</div>
      <div style={{fontSize:12,color:'#EF4444',fontWeight:600,marginBottom:4}}>No disponible</div>
      <div style={{fontSize:11,color:'#94A3B8'}}>{error}</div>
    </div>
  );

  if (!data) return null;

  switch (tabKey) {
    case 'info':      return <HardwareTab  data={data}/>;
    case 'software':  return <SoftwareTab  data={data} onAction={onAction}/>;
    case 'processes': return <ProcessesTab data={data} onAction={onAction}/>;
    case 'disks':     return <DisksTab     data={data}/>;
    case 'network':   return <NetworkTab   data={data}/>;
    case 'events':    return <EventsTab    data={data}/>;
    case 'updates':   return <UpdatesTab   data={data}/>;
    default: return null;
  }
}


/* ── Panel principal ─────────────────────────────────── */

export default function DevicePanel({ deviceInfo, agentAvailable, inventory, monitoring, onAction }) {
  const [activeTab,   setActiveTab]   = useState('info');
  const [agentOnline, setAgentOnline] = useState(false);

  useEffect(() => {
    if (inventory) { setAgentOnline(true); return; }
    fetchAgentData('info').then(d => setAgentOnline(d?.ok === true)).catch(() => setAgentOnline(false));
  }, [inventory]);

  if (!deviceInfo && !agentOnline) return (
    <div style={{padding:'40px 24px',textAlign:'center'}}>
      <Icon name="power_off" size={36} style={{opacity:.3,marginBottom:12,color:"#94A3B8"}} />
      <div style={{fontSize:14,fontWeight:600,color:'#475569',marginBottom:6}}>Sin datos del equipo</div>
      <div style={{fontSize:12,color:'#94A3B8'}}>Instala el agente en el equipo para ver esta información.</div>
    </div>
  );

  return (
    <div>
      {/* Tabs */}
      <div style={{display:'flex',gap:0,marginBottom:20,borderBottom:'2px solid #F1F5F9',overflowX:'auto'}}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{
              padding:'8px 16px', border:'none', background:'none', cursor:'pointer',
              fontSize:12, fontWeight: activeTab===t.key ? 700 : 500,
              color: activeTab===t.key ? 'var(--una-blue)' : '#94A3B8',
              borderBottom: `2px solid ${activeTab===t.key ? 'var(--una-blue)' : 'transparent'}`,
              marginBottom:-2, whiteSpace:'nowrap', transition:'all .15s',
              display:'flex', alignItems:'center', gap:5,
            }}>
            <Icon name={t.icon} size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* Aviso agente offline para pestañas live */}
      {!['info','monitor'].includes(activeTab) && !agentOnline && (
        <div style={{
          marginBottom:14,padding:'10px 14px',
          background:'#FFFBEB',border:'1px solid #FDE68A',
          borderRadius:8,fontSize:12,color:'#B45309',
          display:'flex',alignItems:'center',gap:8,
        }}>
          <span>⚠</span>
          <span>El agente no está activo en este equipo — solo se muestran datos del momento del ticket.</span>
        </div>
      )}

      {/* Contenido */}
      <div style={{minHeight:200}}>
        {activeTab === 'monitor'
          ? <MonitorTab monitoring={monitoring}/>
          : <TabContent key={activeTab} tabKey={activeTab} deviceInfo={deviceInfo ?? {}} inventory={inventory} onAction={onAction}/>
        }
      </div>
    </div>
  );
}
